/**
 * useCompositionClock — drives the hero's authored-time axis `T`.
 *
 * `window.OM_PLAYBACK` in the design source is `{"mode":"loop"}`, so T runs
 * 0 → AUTHORED_TOTAL and wraps, forever. The design engine renders on its own
 * rAF loop; this does the same, with three production concessions the preview
 * harness did not need:
 *
 *   1. **Reduced motion.** `prefers-reduced-motion: reduce` pins T to the Hold
 *      cue, so the house renders fully built and completely still. The scene is
 *      the finished artwork, not a spinner — freezing it at Hold is the honest
 *      still frame, and it is what a user who asked the OS for no motion gets.
 *   2. **Off-screen and hidden tabs.** The loop stops when the hero scrolls out
 *      of view or the tab is hidden. It is a 60fps 3D transform tree; leaving it
 *      running behind six other sections is real battery for nothing, and this
 *      audience is on a mid-range Android.
 *   3. **Frame-time clamping.** After a stall (tab restore, long task) the delta
 *      is capped so T advances by at most one frame's worth rather than jumping
 *      the build several scenes forward in a single step.
 *
 * T is exposed via a ref-backed subscription rather than state: the animation
 * re-renders every frame by design, and routing that through `useState` would
 * schedule a React update per frame from a hook that has no other reason to.
 */
import { useEffect, useRef, useState } from "react";

import { AUTHORED_TOTAL, CUES } from "@/features/landing/hero/timeline";

/** Longest delta we will integrate in one step (~2 frames at 60fps). */
const MAX_FRAME_S = 1 / 30;

export interface CompositionClock {
  /** Attach to the element whose visibility gates the loop. */
  stageRef: React.RefObject<HTMLDivElement | null>;
  /** Authored seconds, 0 → AUTHORED_TOTAL, wrapping. */
  t: number;
}

export function useCompositionClock(): CompositionClock {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [t, setT] = useState(0);

  useEffect(() => {
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      // The finished house, held still.
      setT(CUES.Hold + 0.9);
      return;
    }

    let raf = 0;
    let last = 0;
    let clock = 0;
    let visible = true;
    let onScreen = true;

    const frame = (now: number): void => {
      if (last !== 0) {
        const dt = Math.min((now - last) / 1000, MAX_FRAME_S);
        clock = (clock + dt) % AUTHORED_TOTAL;
        setT(clock);
      }
      last = now;
      raf = window.requestAnimationFrame(frame);
    };

    const start = (): void => {
      if (raf !== 0) return;
      last = 0; // resync so a resumed loop does not integrate the gap
      raf = window.requestAnimationFrame(frame);
    };
    const stop = (): void => {
      if (raf === 0) return;
      window.cancelAnimationFrame(raf);
      raf = 0;
    };
    const sync = (): void => {
      if (visible && onScreen) start();
      else stop();
    };

    const onVisibility = (): void => {
      visible = document.visibilityState !== "hidden";
      sync();
    };
    document.addEventListener("visibilitychange", onVisibility);

    let io: IntersectionObserver | undefined;
    const el = stageRef.current;
    if (el && typeof IntersectionObserver === "function") {
      io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry) {
            onScreen = entry.isIntersecting;
            sync();
          }
        },
        { threshold: 0 },
      );
      io.observe(el);
    }

    sync();

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      io?.disconnect();
    };
  }, []);

  return { stageRef, t };
}
